import { useState } from "react"
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query"
import { Plus, Settings2, CheckCircle2, ChevronRight, Mail, MoreHorizontal } from "lucide-react"
import { useForm } from "react-hook-form"
import { zodResolver } from "@hookform/resolvers/zod"
import { z } from "zod"
import { format } from "date-fns"
import { toast } from "sonner"

import { api } from "@/lib/api"
import { Button } from "@/components/ui/button"
import { Badge } from "@/components/ui/badge"
import { Input } from "@/components/ui/input"
import { Label } from "@/components/ui/label"
import { Textarea } from "@/components/ui/textarea"
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select"
import { Skeleton } from "@/components/ui/skeleton"
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu"
import { Sheet, SheetContent, SheetDescription, SheetFooter, SheetHeader, SheetTitle } from "@/components/ui/sheet"
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle } from "@/components/ui/dialog"

interface FeatureRequest {
  id: string
  title: string
  description: string | null
  status: "pending" | "planned" | "in_progress" | "completed" | "rejected"
  requested_by_email: string | null
  requested_by_name: string | null
  display_name_preference: "full" | "initial" | "anonymous"
  internal_notes: string | null
  planned_updates: string | null
  email_sent_at: string | null
  created_at: string
}

const reqSchema = z.object({
  title: z.string().min(1, "Title is required"),
  description: z.string().optional().nullable(),
  status: z.enum(["pending", "planned", "in_progress", "completed", "rejected"]),
  requested_by_email: z.string().email().optional().or(z.literal("")).nullable(),
  requested_by_name: z.string().optional().nullable(),
  display_name_preference: z.enum(["full", "initial", "anonymous"]),
  internal_notes: z.string().optional().nullable(),
  planned_updates: z.string().optional().nullable(),
})

type ReqForm = z.infer<typeof reqSchema>

export default function AdminFeatureRequestsPage() {
  const qc = useQueryClient()
  const [editing, setEditing] = useState<FeatureRequest | null>(null)
  const [sheetOpen, setSheetOpen] = useState(false)
  const [createOpen, setCreateOpen] = useState(false)

  const { data: requests = [], isLoading } = useQuery<FeatureRequest[]>({
    queryKey: ["admin", "feature-requests"],
    queryFn: async () => {
      const { data } = await api.get("/api/admin/feature-requests")
      return data.data?.featureRequests ?? []
    },
  })

  const create = useMutation({
    mutationFn: async (body: ReqForm) => {
      const { data } = await api.post("/api/admin/feature-requests", body)
      return data.data
    },
    onSuccess: () => {
      toast.success("Feature request created")
      qc.invalidateQueries({ queryKey: ["admin", "feature-requests"] })
      setCreateOpen(false)
      form.reset()
    },
    onError: () => toast.error("Failed to create feature request"),
  })

  const update = useMutation({
    mutationFn: async ({ id, body }: { id: string; body: ReqForm }) => {
      const { data } = await api.patch(`/api/admin/feature-requests/${id}`, body)
      return data.data
    },
    onSuccess: () => {
      toast.success("Feature request updated")
      qc.invalidateQueries({ queryKey: ["admin", "feature-requests"] })
      setSheetOpen(false)
    },
    onError: () => toast.error("Failed to update feature request"),
  })

  const sendEmail = useMutation({
    mutationFn: async (id: string) => {
      const { data } = await api.post(`/api/admin/feature-requests/${id}/send-completion-email`)
      return data
    },
    onSuccess: () => {
      toast.success("Completion email sent successfully!")
      qc.invalidateQueries({ queryKey: ["admin", "feature-requests"] })
    },
    onError: () => toast.error("Failed to send email"),
  })

  const form = useForm<ReqForm>({
    resolver: zodResolver(reqSchema),
    defaultValues: {
      status: "pending",
      display_name_preference: "initial",
    }
  })

  function openCreate() {
    form.reset({
      title: "",
      description: "",
      status: "pending",
      requested_by_email: "",
      requested_by_name: "",
      display_name_preference: "initial",
      internal_notes: "",
      planned_updates: "",
    })
    setCreateOpen(true)
  }

  function openEdit(req: FeatureRequest) {
    setEditing(req)
    form.reset({
      title: req.title,
      description: req.description,
      status: req.status,
      requested_by_email: req.requested_by_email,
      requested_by_name: req.requested_by_name,
      display_name_preference: req.display_name_preference,
      internal_notes: req.internal_notes,
      planned_updates: req.planned_updates,
    })
    setSheetOpen(true)
  }

  function onSubmit(values: ReqForm) {
    if (editing) {
      update.mutate({ id: editing.id, body: values })
    } else {
      create.mutate(values)
    }
  }

  return (
    <div className="p-8 max-w-[1400px] mx-auto space-y-8">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-3xl font-bold tracking-tight">Feature Requests</h1>
          <p className="text-muted-foreground mt-1 text-sm max-w-2xl">
            Manage user feature requests, update implementation status, and notify users via email.
          </p>
        </div>
        <Button onClick={openCreate} className="rounded-xl shadow-sm h-10">
          <Plus className="h-4 w-4 mr-2" />
          Add Request
        </Button>
      </div>

      <div className="bg-card rounded-3xl border border-border overflow-hidden shadow-sm">
        <div className="overflow-x-auto">
          <table className="w-full text-sm text-left">
            <thead className="bg-muted/30 text-xs uppercase font-semibold text-muted-foreground border-b border-border">
              <tr>
                <th className="px-6 py-4">Title</th>
                <th className="px-6 py-4">Requester</th>
                <th className="px-6 py-4">Status</th>
                <th className="px-6 py-4">Date</th>
                <th className="px-6 py-4 text-right">Actions</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-border/50">
              {isLoading ? (
                Array.from({ length: 5 }).map((_, i) => (
                  <tr key={i}>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-[250px]" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-[150px]" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-[80px]" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-[100px]" /></td>
                    <td className="px-6 py-4"><Skeleton className="h-4 w-[30px] ml-auto" /></td>
                  </tr>
                ))
              ) : requests.length === 0 ? (
                <tr>
                  <td colSpan={5} className="px-6 py-12 text-center text-muted-foreground">
                    No feature requests found.
                  </td>
                </tr>
              ) : (
                requests.map((req) => (
                  <tr key={req.id} className="hover:bg-muted/10 transition-colors">
                    <td className="px-6 py-4">
                      <div className="font-medium text-foreground">{req.title}</div>
                      <div className="text-xs text-muted-foreground truncate max-w-xs">{req.description}</div>
                    </td>
                    <td className="px-6 py-4">
                      <div className="flex flex-col">
                        <span className="font-medium">{req.requested_by_name || "Unknown"}</span>
                        <span className="text-xs text-muted-foreground">{req.requested_by_email || "No email"}</span>
                      </div>
                    </td>
                    <td className="px-6 py-4">
                      <Badge variant={
                        req.status === "completed" ? "default" :
                        req.status === "in_progress" ? "secondary" :
                        req.status === "planned" ? "outline" :
                        req.status === "rejected" ? "destructive" : "secondary"
                      } className={
                        req.status === "completed" ? "bg-emerald-500 hover:bg-emerald-600" :
                        req.status === "in_progress" ? "bg-amber-100 text-amber-800 border-amber-200" :
                        ""
                      }>
                        {req.status.replace("_", " ").toUpperCase()}
                      </Badge>
                    </td>
                    <td className="px-6 py-4 text-muted-foreground">
                      {format(new Date(req.created_at), "MMM d, yyyy")}
                    </td>
                    <td className="px-6 py-4 text-right">
                      <DropdownMenu>
                        <DropdownMenuTrigger asChild>
                          <Button variant="ghost" size="icon" className="h-8 w-8 rounded-lg">
                            <MoreHorizontal className="h-4 w-4" />
                          </Button>
                        </DropdownMenuTrigger>
                        <DropdownMenuContent align="end" className="w-48 rounded-xl">
                          <DropdownMenuItem onClick={() => openEdit(req)}>
                            <Settings2 className="h-4 w-4 mr-2 text-muted-foreground" />
                            Manage
                          </DropdownMenuItem>
                          {req.requested_by_email && req.status === "completed" && (
                            <DropdownMenuItem 
                              onClick={() => sendEmail.mutate(req.id)}
                              disabled={sendEmail.isPending}
                            >
                              {req.email_sent_at ? (
                                <CheckCircle2 className="h-4 w-4 mr-2 text-emerald-500" />
                              ) : (
                                <Mail className="h-4 w-4 mr-2 text-muted-foreground" />
                              )}
                              Send Completion Email
                            </DropdownMenuItem>
                          )}
                        </DropdownMenuContent>
                      </DropdownMenu>
                    </td>
                  </tr>
                ))
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Edit Sheet */}
      <Sheet open={sheetOpen} onOpenChange={setSheetOpen}>
        <SheetContent className="sm:max-w-md w-full border-l border-border shadow-2xl p-0 flex flex-col overflow-hidden">
          <SheetHeader className="p-6 border-b border-border bg-muted/20">
            <SheetTitle className="text-xl font-bold flex items-center gap-2">
              <Settings2 className="h-5 w-5 text-indigo-500" />
              Manage Request
            </SheetTitle>
          </SheetHeader>

          <form onSubmit={form.handleSubmit(onSubmit)} className="flex-1 overflow-y-auto">
            <div className="p-6 space-y-6">
              <div className="space-y-4">
                <div>
                  <Label>Title</Label>
                  <Input {...form.register("title")} className="mt-1" />
                </div>
                <div>
                  <Label>Description</Label>
                  <Textarea {...form.register("description")} className="mt-1 min-h-[80px]" />
                </div>
                <div>
                  <Label>Status</Label>
                  <Select 
                    value={form.watch("status")} 
                    onValueChange={(val: any) => form.setValue("status", val)}
                  >
                    <SelectTrigger className="mt-1">
                      <SelectValue placeholder="Select status" />
                    </SelectTrigger>
                    <SelectContent>
                      <SelectItem value="pending">Pending</SelectItem>
                      <SelectItem value="planned">Planned</SelectItem>
                      <SelectItem value="in_progress">In Progress</SelectItem>
                      <SelectItem value="completed">Completed</SelectItem>
                      <SelectItem value="rejected">Rejected</SelectItem>
                    </SelectContent>
                  </Select>
                </div>
                
                <div className="grid grid-cols-2 gap-4">
                  <div>
                    <Label>Requester Name</Label>
                    <Input {...form.register("requested_by_name")} className="mt-1" />
                  </div>
                  <div>
                    <Label>Privacy Preference</Label>
                    <Select 
                      value={form.watch("display_name_preference")} 
                      onValueChange={(val: any) => form.setValue("display_name_preference", val)}
                    >
                      <SelectTrigger className="mt-1">
                        <SelectValue />
                      </SelectTrigger>
                      <SelectContent>
                        <SelectItem value="full">Full Name</SelectItem>
                        <SelectItem value="initial">First Initial Only</SelectItem>
                        <SelectItem value="anonymous">Anonymous</SelectItem>
                      </SelectContent>
                    </Select>
                  </div>
                </div>
                <div>
                  <Label>Requester Email</Label>
                  <Input {...form.register("requested_by_email")} type="email" className="mt-1" />
                </div>

                <div>
                  <Label>Planned Updates (Public info)</Label>
                  <Textarea {...form.register("planned_updates")} className="mt-1" />
                </div>
                <div>
                  <Label>Internal Notes (Admin only)</Label>
                  <Textarea {...form.register("internal_notes")} className="mt-1" />
                </div>
              </div>
            </div>

            <SheetFooter className="p-6 border-t border-border bg-muted/10">
              <Button type="button" variant="ghost" onClick={() => setSheetOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={update.isPending} className="px-8">{update.isPending ? "Saving..." : "Save Changes"}</Button>
            </SheetFooter>
          </form>
        </SheetContent>
      </Sheet>

      {/* Create Dialog */}
      <Dialog open={createOpen} onOpenChange={setCreateOpen}>
        <DialogContent className="sm:max-w-[500px] p-0 overflow-hidden">
          <DialogHeader className="p-6 border-b border-border bg-muted/20">
            <DialogTitle>Add Feature Request</DialogTitle>
            <DialogDescription>Manually enter a feature request received via email.</DialogDescription>
          </DialogHeader>
          <form onSubmit={form.handleSubmit(onSubmit)} className="p-6 space-y-4">
            <div>
              <Label>Title</Label>
              <Input {...form.register("title")} className="mt-1" />
            </div>
            <div>
              <Label>Description</Label>
              <Textarea {...form.register("description")} className="mt-1" />
            </div>
            <div className="grid grid-cols-2 gap-4">
              <div>
                <Label>Requester Name</Label>
                <Input {...form.register("requested_by_name")} className="mt-1" />
              </div>
              <div>
                <Label>Requester Email</Label>
                <Input {...form.register("requested_by_email")} type="email" className="mt-1" />
              </div>
            </div>
            <div>
              <Label>Privacy Preference</Label>
              <Select 
                value={form.watch("display_name_preference")} 
                onValueChange={(val: any) => form.setValue("display_name_preference", val)}
              >
                <SelectTrigger className="mt-1">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="full">Full Name</SelectItem>
                  <SelectItem value="initial">First Initial Only</SelectItem>
                  <SelectItem value="anonymous">Anonymous</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label>Internal Notes</Label>
              <Textarea {...form.register("internal_notes")} className="mt-1" />
            </div>
            <DialogFooter className="mt-6">
              <Button type="button" variant="ghost" onClick={() => setCreateOpen(false)}>Cancel</Button>
              <Button type="submit" disabled={create.isPending}>{create.isPending ? "Creating..." : "Create Request"}</Button>
            </DialogFooter>
          </form>
        </DialogContent>
      </Dialog>
    </div>
  )
}
